import React, { useState, useMemo } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
  Calendar,
  Zap,
  AlertTriangle,
  ShieldAlert,
  UserX,
  Building2,
  X
} from 'lucide-react';

const formatDuration = (mins) => {
  if (mins === 0 || !mins) return '0 sa';
  const h = Math.floor(Math.abs(mins) / 60);
  const m = Math.abs(mins) % 60;
  return m > 0 ? `${mins < 0 ? '-' : ''}${h}s ${m}d` : `${mins < 0 ? '-' : ''}${h} sa`;
};

const StatChip = ({ icon: Icon, label, value, colorClass, dark = false }) => (
  <div className={`flex flex-col items-center rounded-lg px-1.5 py-1 ${dark ? 'bg-white/10 border border-white/10' : 'bg-slate-50 border border-slate-200/80 dark:bg-slate-950/50 dark:border-slate-700'}`}>
    <div className={`flex items-center gap-1 text-[10px] ${dark ? 'text-slate-300' : 'text-slate-400 dark:text-slate-500'}`}>
      <Icon className={`h-2.5 w-2.5 ${colorClass}`} />
      <span>{label}</span>
    </div>
    <div className={`text-[11px] font-bold leading-tight ${dark ? 'text-white' : 'text-slate-800 dark:text-white'}`}>{value}</div>
  </div>
);

const QUALITY_TABS = [
  { id: '', label: 'Tümü' },
  { id: 'risky', label: '⚠️ Riskli' },
  { id: 'error', label: '🔴 Hata' },
  { id: 'absent', label: '🚫 Devamsız' },
];

const AttendanceSidebar = ({
  employees,
  loading,
  pagination,
  pageSize,
  onPageSizeChange,
  onPageChange,
  onSearch,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  activeEmployeeId,
  onSelectEmployee,
  monthLabel,
  qualityFilter = '',
  onQualityFilterChange,
  departments = [],
  selectedDepartmentId = '',
  onDepartmentChange,
}) => {
  // Yerel arama terimi — hem backend'e gönderilir hem de lokal filtre uygular
  const [localSearch, setLocalSearch] = useState('');

  const handleSearchChange = (val) => {
    setLocalSearch(val);
    onSearch(val);
  };

  // Lokal filtre: backend'in dönmediği anlık tuş vuruşlarında ve sekme geçişlerinde hızlı filtreler
  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (qualityFilter === 'error') {
      list = list.filter((emp) => (emp.qualityErrorCount || 0) > 0);
    } else if (qualityFilter === 'risky') {
      list = list.filter((emp) => (emp.qualityErrorCount || 0) > 0 || (emp.qualityWarningCount || 0) > 0);
    } else if (qualityFilter === 'absent') {
      list = list.filter((emp) => (emp.absentDayCount || 0) > 0);
    }
    if (!localSearch.trim()) return list;
    const term = localSearch.trim().toLocaleLowerCase('tr-TR');
    return list.filter((emp) => {
      const dept = String(emp.departmentName || emp.unitName || '').toLocaleLowerCase('tr-TR');
      const name = String(emp.fullName || '').toLocaleLowerCase('tr-TR');
      const reg  = String(emp.identityNumber || emp.nationalId || emp.registrationNumber || '').toLocaleLowerCase('tr-TR');
      return name.includes(term) || reg.includes(term) || dept.includes(term);
    });
  }, [employees, localSearch, qualityFilter]);

  const isAllSelected =
    filteredEmployees.length > 0 &&
    filteredEmployees.every((e) => selectedIds.includes(e.employeeId));

  return (
    <div className="rounded-3xl border border-slate-200/70 bg-white shadow-lg shadow-slate-200/60 dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
      {/* Header */}
      <div className="border-b border-slate-200/70 px-4 pb-3 pt-4 dark:border-slate-700">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold tracking-tight text-slate-900 dark:text-white">Personel Listesi</h3>
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                {pagination.totalCount || 0}
              </span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{monthLabel} puantaj özeti</p>
          </div>
          <select
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
            className="h-8 rounded-xl border border-slate-200 bg-slate-50 px-2 text-xs font-medium text-slate-700 outline-none transition focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Sayfa başına personel"
          >
            <option value="50">50 / syf</option>
            <option value="100">100 / syf</option>
            <option value="200">200 / syf</option>
          </select>
        </div>

        {/* Hızlı Risk & Kalite Filtre Sekmeleri */}
        {onQualityFilterChange && (
          <div className="mt-2.5 grid grid-cols-4 gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
            {QUALITY_TABS.map((tab) => {
              const isActive = qualityFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => onQualityFilterChange(tab.id)}
                  className={`rounded-lg py-1.5 text-[10px] font-bold transition-all ${
                    isActive
                      ? 'bg-white text-slate-900 shadow-sm dark:bg-indigo-600 dark:text-white'
                      : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Departman Filtresi */}
        {departments.length > 0 && onDepartmentChange && (
          <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-800/80">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <select
              value={String(selectedDepartmentId || '')}
              onChange={(e) => onDepartmentChange(e.target.value ? parseInt(e.target.value, 10) : '')}
              className="w-full bg-transparent text-xs font-medium text-slate-700 outline-none dark:text-slate-200"
            >
              <option value="" className="dark:bg-slate-800">Tüm Departmanlar / Birimler</option>
              {departments.map((d) => (
                <option key={d.id} value={String(d.id)} className="dark:bg-slate-800">
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Arama */}
        <div className="mt-2 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-800/80">
          <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <input
            className="w-full bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200"
            placeholder="Ad, sicil veya bölüm ile ara..."
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => handleSearchChange('')}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Tümünü seç */}
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60">
          <div className="flex items-center justify-between gap-2">
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Sayfadakileri seç
            </label>
            <div className="flex items-center gap-1.5">
              {selectedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => onSelectAll(false)}
                  className="text-[10px] font-semibold text-slate-500 hover:text-rose-600 dark:text-slate-400"
                >
                  Temizle
                </button>
              )}
              <span className="rounded-lg bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white dark:bg-indigo-600">
                {selectedIds.length} seçili
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Personel listesi */}
      <div className="relative flex flex-col min-h-[400px] h-[calc(100vh-330px)]">
        {loading && (
          <div className="absolute inset-x-0 top-0 bottom-0 z-20 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] dark:bg-slate-900/60 transition-all rounded-b-3xl">
            <div className="flex flex-col items-center gap-3 rounded-2xl bg-white p-5 shadow-2xl dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600/15 border-t-indigo-600"></div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">Yükleniyor</span>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-2.5 space-y-1.5 custom-scrollbar">
          {!loading && filteredEmployees.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-center px-4">
              <div className="text-xs font-bold uppercase tracking-[0.15em] opacity-60">Kayıt bulunamadı</div>
              {qualityFilter && (
                <button
                  type="button"
                  onClick={() => onQualityFilterChange?.('')}
                  className="mt-2 rounded-lg bg-indigo-50 px-3 py-1 text-[11px] font-bold text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300"
                >
                  Filtreyi Temizle (Tümünü Göster)
                </button>
              )}
            </div>
          )}

          {filteredEmployees.map((emp) => {
            const active = activeEmployeeId === emp.employeeId;
            const checked = selectedIds.includes(emp.employeeId);
            const errCount = emp.qualityErrorCount || 0;
            const warnCount = emp.qualityWarningCount || 0;
            const absentCount = emp.absentDayCount || 0;

            return (
              <div
                key={emp.employeeId}
                className={`w-full rounded-2xl border p-2.5 transition ${
                  active
                    ? 'border-slate-900 bg-slate-900 text-white shadow-xl dark:border-indigo-500 dark:bg-slate-950'
                    : errCount > 0
                      ? 'border-rose-200/80 bg-white hover:border-rose-300 hover:shadow-sm dark:border-rose-900/50 dark:bg-slate-900/70'
                      : warnCount > 0
                        ? 'border-amber-200/80 bg-white hover:border-amber-300 hover:shadow-sm dark:border-amber-900/50 dark:bg-slate-900/70'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-900/70 dark:hover:border-slate-600'
                }`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelect(emp.employeeId);
                    }}
                    className={`mt-1 h-4 w-4 shrink-0 rounded border ${
                      active
                        ? 'border-white/30 bg-black/20 text-indigo-500 focus:ring-white/20'
                        : 'border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500'
                    }`}
                  />

                  <button className="min-w-0 flex-1 text-left" onClick={() => onSelectEmployee(emp)}>
                    {/* İsim + Sicil + Risk Rozetleri */}
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className={`text-xs font-semibold leading-tight ${active ? 'text-white' : 'text-slate-900 dark:text-white'}`}>
                            {emp.fullName}
                          </span>
                          <span className={`text-[10px] font-mono ${active ? 'text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}>
                            #{emp.employeeId}
                          </span>
                        </div>
                        <div className={`text-[10px] truncate ${active ? 'text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}>
                          {emp.departmentName || emp.unitName || 'Birim Atanmamış'}
                        </div>
                      </div>

                      {/* Risk & Kalite Rozetleri */}
                      <div className="flex flex-wrap items-center justify-end gap-1 shrink-0">
                        {errCount > 0 && (
                          <span
                            title={`${errCount} günde kritik puantaj hatası`}
                            className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-black ${
                              active
                                ? 'bg-rose-500 text-white'
                                : 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300'
                            }`}
                          >
                            <ShieldAlert className="h-2.5 w-2.5" />
                            {errCount} Hata
                          </span>
                        )}
                        {warnCount > 0 && (
                          <span
                            title={`${warnCount} günde puantaj uyarısı`}
                            className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-bold ${
                              active
                                ? 'bg-amber-500 text-white'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                            }`}
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            {warnCount} Uyarı
                          </span>
                        )}
                        {absentCount > 0 && (
                          <span
                            title={`${absentCount} gün devamsızlık`}
                            className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-bold ${
                              active
                                ? 'bg-white/15 text-rose-200'
                                : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                            }`}
                          >
                            <UserX className="h-2.5 w-2.5" />
                            {absentCount} Yok
                          </span>
                        )}
                        {errCount === 0 && warnCount === 0 && absentCount === 0 && (
                          <span className={`shrink-0 rounded-lg px-1.5 py-0.5 text-[9px] font-semibold whitespace-nowrap ${
                            active ? 'bg-white/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          }`}>
                            ✓ Temiz
                          </span>
                        )}
                      </div>
                    </div>

                    {/* İstatistik chip'leri — tek satır 4 chip */}
                    <div className="mt-1.5 grid grid-cols-4 gap-1">
                      <StatChip icon={Clock}        label="ES"  value={formatDuration(emp.totalUndertimeMinutes)} colorClass="text-rose-500"   dark={active} />
                      <StatChip icon={Zap}          label="FM"  value={formatDuration(emp.totalOvertimeMinutes)}  colorClass="text-emerald-500" dark={active} />
                      <StatChip icon={Calendar}     label="ÇG"  value={String(emp.workedDayCount || 0)}           colorClass="text-blue-500"    dark={active} />
                      <StatChip icon={CheckCircle2} label="HT"  value={String(emp.weekOffCount || 0)}             colorClass="text-indigo-500"  dark={active} />
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sayfalama */}
      <div className="flex items-center justify-between border-t border-slate-200/70 px-3 py-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        <span>
          {(pagination.pageNumber - 1) * pageSize + (employees.length ? 1 : 0)}–{(pagination.pageNumber - 1) * pageSize + employees.length} / {pagination.totalCount}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            disabled={pagination.pageNumber <= 1 || loading}
            onClick={() => onPageChange(pagination.pageNumber - 1)}
            className="rounded-xl border border-slate-200 p-1.5 disabled:opacity-40 dark:border-slate-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="px-1.5 font-semibold text-slate-700 dark:text-slate-300">
            {pagination.pageNumber} / {pagination.totalPages}
          </span>
          <button
            disabled={pagination.pageNumber >= pagination.totalPages || loading}
            onClick={() => onPageChange(pagination.pageNumber + 1)}
            className="rounded-xl border border-slate-200 p-1.5 disabled:opacity-40 dark:border-slate-700"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSidebar;
